import React, {useEffect, useMemo, useState} from 'react';

import {useNavigate} from 'react-router';

import {useAction, useActions, useModule, useSelectorWithRefresh} from '@/store/kysely_module';
import {useCurrentWorkspaceId} from '@/store/modules/workspaces_module';
import {DBObjects} from '@/store/store_types';
import {StripedTable} from '../components/striped_table';
import {useModals} from '../components/modals/modals_provider';
import {IconPencil, IconEyeOff, IconArrowUp, IconArrowDown, IconEye, IconFolder, IconPlus} from '@tabler/icons-react';
import {Button, Group, Grid, Table, Stack, Title} from '@mantine/core';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {DashboardCard} from '@/components/new_dashboard/components/DashboardCard';
import {DashboardLayout} from '@/components/new_dashboard/components/DashboardLayout';
import {useIsMobile} from '@/utils/user_agent';
import {Selectors} from '@/store/selectors/selectors';
import {PlayIcon} from '@/modules/audio_player/audio_player_module';
import {findBounceFileWithPriority, isAudioFile} from '@/utils/file_utils';
import {useCurrentUser} from '../hooks/useCurrentUser';
import {formatTimeAgo, formatDuration} from '@/components/new_dashboard/utils/timeFormatting';

type Props = {
    workspaceId: string;
};

export const ProjectSelectionPage = ({workspaceId}: Props) => {
    const [showHidden, setShowHidden] = useState(false);
    const [projects, refreshProjects] = useSelectorWithRefresh('getProjectsForWorkspace', {workspace_id: workspaceId, topLevelOnly: true, includeHidden: false}, [], {workspace_id: workspaceId});
    const [allProjects, refreshAllProjects] = useSelectorWithRefresh('getProjectsForWorkspace', {workspace_id: workspaceId, topLevelOnly: true, includeHidden: true}, [], {workspace_id: workspaceId});

    const currentUser = useCurrentUser();
    const [workspace] = useSelectorWithRefresh('getWorkspace', {workspace_id: workspaceId}, null, {workspace_id: workspaceId});
    const isWorkspaceOwner = currentUser?.id === workspace?.owner_id;
    const hasHiddenProjects = allProjects.filter(p => p.hidden_at !== null).length > 0;
    const hiddenProjects = allProjects.filter(p => p.hidden_at !== null);

    const refreshData = () => {
        refreshProjects();
        refreshAllProjects();
    };

    useEffect(() => {
        // TODO: need to rethink data fetching layer
        refreshData();
    }, [workspaceId, showHidden]);

    const navigate = useNavigate();
    const actions = {
        createProject: useAction('createProject'),
    };

    const modals = useModals();

    const navigateToProject = (projectId: string) => {
        navigateToPathForReason(navigate, 'navigate_to_project_page_from_project_selection_page', `/workspaces/${workspaceId}/projects/${projectId}`);
    }

    const createNewProject = async () => {
        modals.openModal('TextInputModal', {
            title: 'New project',
            inputName: 'Project name',
            onSubmit: async (projectName) => {
                const project = await actions.createProject({name: projectName, workspace_id: workspaceId}, {workspace_id: workspaceId});
                refreshData();
                navigateToProject(project.id);
            },
        });
    };

    const isMobile = useIsMobile();

    return (
        <div style={{
            width: '100%',
            padding: isMobile ? '0.5rem' : '2rem',
            minHeight: '70vh'
        }}>
            <div style={{
                width: '100%',
                minWidth: isMobile ? 'auto' : '800px',
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <Stack gap="xl">
                    <DashboardCard
                        title="Projects"
                        icon={<IconFolder size={18} />}
                        headerActions={
                            <Button
                                size="xs"
                                leftSection={<IconPlus size={16} />}
                                onClick={createNewProject}
                            >
                                New Project
                            </Button>
                        }
                    >
                        <ProjectTable
                            projects={projects}
                            onClick={navigateToProject}
                            refresh={refreshData}
                            isWorkspaceOwner={isWorkspaceOwner}
                            showHidden={false}
                            hasHiddenProjects={hasHiddenProjects}
                        />
                    </DashboardCard>

                    {showHidden && hiddenProjects.length > 0 && (
                        <DashboardCard
                            title="Hidden Projects"
                            icon={<IconEyeOff size={18} />}
                        >
                            <ProjectTable
                                projects={hiddenProjects}
                                onClick={navigateToProject}
                                refresh={refreshData}
                                isWorkspaceOwner={isWorkspaceOwner}
                                showHidden={true}
                                hasHiddenProjects={hasHiddenProjects}
                            />
                        </DashboardCard>
                    )}

                    {isWorkspaceOwner && hasHiddenProjects && (
                        <Group>
                            <Button
                                variant="subtle"
                                size="sm"
                                leftSection={showHidden ? <IconEye size={16} /> : <IconEyeOff size={16} />}
                                onClick={() => setShowHidden(!showHidden)}
                            >
                                {showHidden ? 'Hide hidden projects' : 'Show hidden projects'}
                            </Button>
                        </Group>
                    )}
                </Stack>
            </div>
        </div>
    );
};

type ProjectTableProps = {
    projects: ProjectsWithFiles;
    onClick: (projectId: string) => void;
    createNew?: () => void;
    refresh: () => void;
    isWorkspaceOwner?: boolean;
    showHidden?: boolean;
    parentProjectId?: string; // For subprojects
    hasHiddenProjects?: boolean;
}

type ProjectsWithFiles = Awaited<ReturnType<Selectors['getProject']>>['sub_projects'] | Awaited<ReturnType<Selectors['getProjectsForWorkspace']>>;

export const ProjectTable = (props: ProjectTableProps) => {
    // const columns: React.ComponentProps<typeof StripedTable>['columns'] = useMemo(() => [
    //     {name: 'Name', sortable: true},
    //     {name: 'Type'},
    // ], []);

    const modals = useModals();
    const actions = {
        updateProjectName: useAction('updateProjectName'),
        hideProject: useAction('hideProject'),
        showProject: useAction('showProject'),
        reorderProjects: useAction('reorderProjects'),
        reorderSubProjects: useAction('reorderSubProjects'),
    };

    const isMobile = useIsMobile();

    if (props.projects.length === 0) {
        return (
            <Stack align="center" py="xl">
                <Title order={5} c="dimmed">No projects yet</Title>
            </Stack>
        );
    }

    const handleProjectClick = (projectId: string, e: React.MouseEvent) => {
        props.onClick(projectId);
    };

    return (
        <div style={{overflowX: 'auto'}}>
            <Table striped highlightOnHover withRowBorders>
                {!isMobile && (
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{width: '80px', padding: '8px'}}></Table.Th>
                            <Table.Th style={{minWidth: '150px', textAlign: 'left'}}></Table.Th>
                            <Table.Th style={{width: '140px', textAlign: 'right'}}>Created</Table.Th>
                            <Table.Th style={{width: '140px', textAlign: 'right'}}>Last Updated</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                )}
                <Table.Tbody>
                    {props.projects.map((project) => {
                        const audioFile = findBounceFileWithPriority(project.name, project.files);
                        const fileDuration = audioFile ? audioFile.duration : null;
                        const lastActivityTime = project.updated_at || project.created_at;

                        return (
                            <Table.Tr key={project.id} style={{cursor: 'pointer'}} onClick={(e) => handleProjectClick(project.id, e)}>
                                <Table.Td style={{width: '80px', padding: isMobile ? '6px' : '8px'}}>
                                    <Group gap="xs" wrap="nowrap" align="center">
                                        {audioFile ? (
                                            <PlayIcon fileInfo={audioFile} size={16} />
                                        ) : (
                                            <div style={{width: 16, height: 16}} />
                                        )}
                                        {fileDuration && (
                                            <Group gap={4} wrap="nowrap">
                                                <span style={{fontSize: '0.875rem'}}>
                                                    {formatDuration(typeof fileDuration === 'string' ? parseInt(fileDuration) : fileDuration)}
                                                </span>
                                            </Group>
                                        )}
                                    </Group>
                                </Table.Td>
                                <Table.Td style={{textAlign: 'left', padding: isMobile ? '6px' : '8px'}}>
                                    <Group gap="xs" align="center">
                                        <span style={{fontSize: '0.875rem'}}>
                                            {project.name}
                                        </span>
                                    </Group>
                                </Table.Td>
                                {!isMobile && (
                                    <Table.Td style={{textAlign: 'right', padding: '8px'}}>
                                        <span style={{fontSize: '0.875rem', color: 'var(--mantine-color-dimmed)'}}>
                                            {formatTimeAgo(project.created_at, isMobile)}
                                        </span>
                                    </Table.Td>
                                )}
                                <Table.Td style={{textAlign: 'right', padding: isMobile ? '6px' : '8px'}}>
                                    <span style={{fontSize: '0.875rem', color: 'var(--mantine-color-dimmed)'}}>
                                        {formatTimeAgo(lastActivityTime, isMobile)}
                                    </span>
                                </Table.Td>
                            </Table.Tr>
                        );
                    })}
                </Table.Tbody>
            </Table>
            {/* TODO: Add back action menus for edit, hide, reorder */}
            {/* <div style={{display: 'none'}}>
                {props.projects.map(projectInfo => {
                    const projectIndex = props.projects.findIndex(p => p.id === projectInfo.id);
                    const canMoveUp = projectIndex > 0;
                    const canMoveDown = projectIndex < props.projects.length - 1;

                    return [
                        {
                            label: 'Edit name',
                            icon: <IconPencil size={16}/>,
                            action: () => {
                                modals.openModal('TextInputModal', {
                                    title: 'Rename project',
                                    inputName: 'Project name',
                                    initialValue: projectInfo.name,
                                    onSubmit: async (projectName) => {
                                        await actions.updateProjectName({project_id: projectInfo.id, new_name: projectName}, {workspace_id: projectInfo.workspace_id});
                                        props.refresh();
                                    },
                                });
                            },
                        },
                        ...(props.isWorkspaceOwner ? [
                            ...(projectInfo.hidden_at && props.showHidden ? [{
                                label: 'Show',
                                icon: <IconEye size={16}/>,
                                action: async () => {
                                    await actions.showProject({project_id: projectInfo.id, parent_id: props.parentProjectId}, {workspace_id: projectInfo.workspace_id});
                                    props.refresh();
                                },
                            }] : [{
                                label: 'Hide',
                                icon: <IconEyeOff size={16}/>,
                                action: async () => {
                                    await actions.hideProject({project_id: projectInfo.id, parent_id: props.parentProjectId}, {workspace_id: projectInfo.workspace_id});
                                    props.refresh();
                                },
                            }])
                        ] : []),
                        ...(canMoveUp ? [{
                            label: 'Move up',
                            icon: <IconArrowUp size={16}/>,
                            action: async () => {
                                const currentProjects = [...props.projects];
                                const currentIndex = currentProjects.findIndex(p => p.id === projectInfo.id);
                                if (currentIndex > 0) {
                                    [currentProjects[currentIndex - 1], currentProjects[currentIndex]] = [currentProjects[currentIndex], currentProjects[currentIndex - 1]];

                                    const projectOrders = currentProjects.map((project, index) => ({
                                        project_id: project.id,
                                        index: index,
                                    }));

                                    if (props.parentProjectId) {
                                        await actions.reorderSubProjects({
                                            parent_project_id: props.parentProjectId,
                                            project_orders: projectOrders,
                                        }, {workspace_id: projectInfo.workspace_id});
                                    } else {
                                        await actions.reorderProjects({
                                            workspace_id: projectInfo.workspace_id,
                                            project_orders: projectOrders,
                                        }, {workspace_id: projectInfo.workspace_id});
                                    }
                                    props.refresh();
                                }
                            },
                        }] : []),
                        ...(canMoveDown ? [{
                            label: 'Move down',
                            icon: <IconArrowDown size={16}/>,
                            action: async () => {
                                const currentProjects = [...props.projects];
                                const currentIndex = currentProjects.findIndex(p => p.id === projectInfo.id);
                                if (currentIndex < currentProjects.length - 1) {
                                    [currentProjects[currentIndex], currentProjects[currentIndex + 1]] = [currentProjects[currentIndex + 1], currentProjects[currentIndex]];

                                    const projectOrders = currentProjects.map((project, index) => ({
                                        project_id: project.id,
                                        index: index,
                                    }));

                                    if (props.parentProjectId) {
                                        await actions.reorderSubProjects({
                                            parent_project_id: props.parentProjectId,
                                            project_orders: projectOrders,
                                        }, {workspace_id: projectInfo.workspace_id});
                                    } else {
                                        await actions.reorderProjects({
                                            workspace_id: projectInfo.workspace_id,
                                            project_orders: projectOrders,
                                        }, {workspace_id: projectInfo.workspace_id});
                                    }
                                    props.refresh();
                                }
                            },
                        }] : []),
                    ];
                })}
            </div> */}
        </div>
    );
};
